import { z } from "zod";

export const activityFormatSchema = z.enum([
  "article",
  "video",
  "course",
  "book",
  "podcast",
  "practice",
  "other",
]);
export const libraryViewSchema = z.enum(["active", "archived"]);
export const collectionNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a collection name.")
  .max(80, "Keep the collection name under 80 characters.");

export const libraryQuerySchema = z.object({
  search: z.string().trim().max(120).default(""),
  collectionId: z.uuid().optional(),
  view: libraryViewSchema.default("active"),
  format: activityFormatSchema.optional(),
  difficulty: z.union([z.coerce.number().int().min(1).max(5), z.literal("unrated")]).optional(),
  unscheduled: z
    .union([z.literal(true), z.literal("true")])
    .transform(() => true as const)
    .optional(),
});

export const createCollectionInputSchema = z.object({
  name: collectionNameSchema,
});
export const renameCollectionInputSchema = createCollectionInputSchema;
export const deleteCollectionInputSchema = z.object({
  fallback: z.literal("unassign").optional(),
});
export const activityIdParamsSchema = z.object({
  activityId: z.uuid(),
});
export const collectionIdParamsSchema = z.object({
  collectionId: z.uuid(),
});

export const collectionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  activeCount: z.number().int().nonnegative(),
  archivedCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const activitySummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  summary: z.string().nullable(),
  collection: z
    .object({
      id: z.uuid(),
      name: z.string(),
    })
    .nullable(),
  format: activityFormatSchema,
  estimatedMinutes: z.number().int().positive(),
  difficulty: z.number().int().min(1).max(5).nullable(),
  interest: z.number().int().min(1).max(5).nullable(),
  sourceName: z.string().nullable(),
  resourceTitle: z.string().nullable(),
  resourceUrl: z.string().nullable(),
  favoriteAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  updatedAt: z.string(),
  hasFlashcards: z.boolean(),
});

export const libraryResponseSchema = z.object({
  activities: z.array(activitySummarySchema),
  collections: z.array(collectionSummarySchema),
  counts: z.object({
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
  }),
});

export const collectionMutationResponseSchema = z.object({
  collection: collectionSummarySchema,
});
export const deleteCollectionResponseSchema = z.object({
  deletedCollectionId: z.uuid(),
  unassignedActivityCount: z.number().int().nonnegative(),
});
export const activityArchiveResponseSchema = z.object({
  activityId: z.uuid(),
  archivedAt: z.string().nullable(),
});
export const deleteActivityResponseSchema = z.object({
  deletedActivityId: z.uuid(),
});

export type ActivityFormat = z.infer<typeof activityFormatSchema>;
export type LibraryView = z.infer<typeof libraryViewSchema>;
export type LibraryQuery = z.infer<typeof libraryQuerySchema>;
export type CollectionSummary = z.infer<typeof collectionSummarySchema>;
export type ActivitySummary = z.infer<typeof activitySummarySchema>;
export type LibraryResponse = z.infer<typeof libraryResponseSchema>;
export type CreateCollectionInput = z.infer<typeof createCollectionInputSchema>;
export type RenameCollectionInput = z.infer<typeof renameCollectionInputSchema>;
export type DeleteCollectionInput = z.infer<typeof deleteCollectionInputSchema>;
export type CollectionMutationResponse = z.infer<typeof collectionMutationResponseSchema>;
export type DeleteCollectionResponse = z.infer<typeof deleteCollectionResponseSchema>;
export type ActivityArchiveResponse = z.infer<typeof activityArchiveResponseSchema>;
export type DeleteActivityResponse = z.infer<typeof deleteActivityResponseSchema>;
